import { Center, DefaultMantineColor, StyleProp, Text } from '@mantine/core';
import { point, distance } from '@turf/turf';
import { MultiPoint, Point } from 'geojson';
import { useMemo } from 'react';
import { Marker } from 'react-map-gl';

export const SAMPLES_LAYER_ID = 'samples-layer';

export type SamplesFeatureCollection = GeoJSON.FeatureCollection<
  MultiPoint | Point,
  {
    color?: string;
    label?: string;
    [key: string]: unknown;
  }
>;

/* eslint-disable-next-line */
export interface SamplesLayerProps {
  data: SamplesFeatureCollection;
  size: number;
  radius?: number | string;
  color?: StyleProp<DefaultMantineColor>;
  sourceId?: string;
  layerId?: string;
  withLabel?: boolean;
  groupingThreshold?: number; // Distance threshold in meters for grouping markers
  maxGroupRadius?: number; // Maximum radius in pixels for arranging grouped markers
  onClick?: (params: {
    id: string | number | null | undefined;
    lngLat: {
      lng: number;
      lat: number;
    };
  }) => void;
}

interface GroupedMarker {
  originalFeature: GeoJSON.Feature<
    Point,
    {
      color?: string;
      label?: string;
      [key: string]: unknown;
    }
  >;
  displayPosition: [number, number]; // [longitude, latitude]
}

interface MarkerGroup {
  centerPosition: [number, number]; // [longitude, latitude]
  markers: GroupedMarker[];
  isGrouped: boolean; // Whether this group has multiple markers
  groupCount: number; // Number of markers in the group
}

export function SamplesLayers({
  data,
  size = 15,
  radius = '50%',
  color,
  withLabel,
  groupingThreshold = 10, // 10 meters threshold for grouping
  maxGroupRadius = 30, // 30 pixels radius for arranging grouped markers
  onClick,
}: SamplesLayerProps) {
  // Group markers by proximity
  const groupedMarkers = useMemo(() => {
    const groups: MarkerGroup[] = [];
    const processedFeatures = new Set<string>();

    data.features.forEach((feature) => {
      const coords = feature.geometry.coordinates as [number, number];
      const featureKey = `${coords[0]}-${coords[1]}`;

      if (processedFeatures.has(featureKey)) return;

      // Find all features at the same or very close positions
      const nearbyFeatures = data.features.filter((otherFeature) => {
        const otherCoords = otherFeature.geometry.coordinates as [
          number,
          number,
        ];
        const otherKey = `${otherCoords[0]}-${otherCoords[1]}`;
        if (processedFeatures.has(otherKey)) return false;

        const dist = distance(point(coords), point(otherCoords), {
          units: 'meters',
        });

        return dist <= groupingThreshold;
      });

      // Mark these features as processed
      nearbyFeatures.forEach((f) => {
        const fCoords = f.geometry.coordinates as [number, number];
        const key = `${fCoords[0]}-${fCoords[1]}`;
        processedFeatures.add(key);
      });

      // Create group
      const centerPosition = coords;
      const markers: GroupedMarker[] = [];

      if (nearbyFeatures.length === 1) {
        // Single marker - keep at original position
        markers.push({
          originalFeature: nearbyFeatures[0] as GeoJSON.Feature<
            Point,
            {
              color?: string;
              label?: string;
              [key: string]: unknown;
            }
          >,
          displayPosition: centerPosition,
        });
      } else {
        // Multiple markers - arrange in circle
        nearbyFeatures.forEach((f, index) => {
          const angle = (2 * Math.PI * index) / nearbyFeatures.length;
          const offsetX = (maxGroupRadius / 111000) * Math.cos(angle); // Convert pixels to approximate degrees
          const offsetY = (maxGroupRadius / 111000) * Math.sin(angle);

          markers.push({
            originalFeature: f as GeoJSON.Feature<
              Point,
              {
                color?: string;
                label?: string;
                [key: string]: unknown;
              }
            >,
            displayPosition: [
              centerPosition[0] + offsetX,
              centerPosition[1] + offsetY,
            ],
          });
        });
      }

      groups.push({
        centerPosition,
        markers,
        isGrouped: nearbyFeatures.length > 1,
        groupCount: nearbyFeatures.length,
      });
    });

    return groups;
  }, [data.features, groupingThreshold, maxGroupRadius]);

  return (
    <>
      {groupedMarkers.flatMap((group) => [
        // Render center marker for grouped items
        ...(group.isGrouped
          ? [
              <Marker
                anchor="center"
                key={`center-${group.centerPosition[0]}-${group.centerPosition[1]}`}
                latitude={group.centerPosition[1]}
                longitude={group.centerPosition[0]}
              >
                <Center
                  bg="white"
                  style={{
                    borderRadius: '50%',
                    width: 8,
                    height: 8,
                    cursor: 'default',
                  }}
                ></Center>
              </Marker>,
            ]
          : []),
        // Render individual markers
        ...group.markers.map((marker, index) => (
          <Marker
            anchor="center"
            key={`${marker.originalFeature.id}-${index}`}
            latitude={marker.displayPosition[1]}
            longitude={marker.displayPosition[0]}
            onClick={(event) => {
              onClick?.({
                id: marker.originalFeature.id,
                lngLat: event.target.getLngLat(),
              });
            }}
          >
            <Center
              bg={
                color ||
                (marker.originalFeature.properties
                  .color as StyleProp<DefaultMantineColor>)
              }
              style={{
                backgroundColor: marker.originalFeature.properties
                  .color as string,
                borderRadius: marker.originalFeature.properties
                  .radius as string,
                width: size,
                height: size,
                border: marker.originalFeature.properties.isSelected
                  ? '3px solid red'
                  : '3px solid white',
                cursor: onClick ? 'pointer' : 'default',
                opacity: group.isGrouped ? 0.9 : 1, // Slight transparency for grouped markers
              }}
            >
              {withLabel ? (
                <Text c="white" fw="bold" lh={1} size="md">
                  {marker.originalFeature.properties.label as string}
                </Text>
              ) : null}
            </Center>
          </Marker>
        )),
      ])}
    </>
  );
}

export default SamplesLayers;
